import { useState, useEffect, useMemo } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { Device, DevicesState, InstalledApp, DeviceStatusBar, UninstallRequest, DeviceManagement } from '../types';
import { deviceService } from '../services/deviceService';
import { parseUpdateStatus } from '../utils/updates';
import { updateService } from '../services/updateService';

// O launcher reporta a cada 5min; a margem cobre um batimento perdido
const ONLINE_THRESHOLD_MS = 10 * 60 * 1000;
const FRESHNESS_TICK_MS = 60 * 1000;

export interface FailedUpdate {
  errorMessage: string;
  updatedAt: number;
}

export function useDevices(isAuthenticated: boolean = false): DevicesState & {
  pendingUpdates: Record<string, boolean>,
  failedUpdates: Record<string, FailedUpdate>,
  cancelUpdate: (deviceId: string) => Promise<boolean>
} {
  const [rawDevices, setRawDevices] = useState<Device[]>([]);
  const [now, setNow] = useState<number>(() => Date.now());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUpdates, setPendingUpdates] = useState<Record<string, boolean>>({});
  const [failedUpdates, setFailedUpdates] = useState<Record<string, FailedUpdate>>({});

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(true);
      return () => {};
    }

    const devicesRef = ref(database, 'devices');

    const unsubscribe = onValue(devicesRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const devicesData = snapshot.val();
          if (typeof devicesData === 'object' && devicesData !== null) {
            const devicesArray: Device[] = Object.entries(devicesData).map(([key, value]: [string, any]) => {
              // Processar aplicativos instalados, se disponível
              let installedApps: InstalledApp[] | undefined = undefined;
              
              if (value.installedApps && typeof value.installedApps === 'object') {
                installedApps = [];
                // Processa estrutura onde os apps estão como app_0, app_1, etc.
                Object.keys(value.installedApps).forEach(appKey => {
                  const appData = value.installedApps[appKey];
                  if (appData && typeof appData === 'object') {
                    installedApps?.push({
                      packageName: appData.packageName || '',
                      appName: appData.name || '',
                      versionName: appData.versionName || '',
                      versionCode: Number(appData.versionCode) || 0,
                      installedDate: appData.installedDate ? Number(appData.installedDate) : undefined,
                      lastUpdated: appData.lastUpdated ? Number(appData.lastUpdated) : undefined,
                      isSystemApp: Boolean(appData.isSystemApp)
                    });
                  }
                });
              }

              // Processar solicitações de desinstalação, se disponível
              let uninstallRequests: Record<string, UninstallRequest> | undefined = undefined;
              
              if (value.uninstallRequests && typeof value.uninstallRequests === 'object') {
                uninstallRequests = {};
                Object.entries(value.uninstallRequests).forEach(([requestKey, requestData]: [string, any]) => {
                  if (requestData && typeof requestData === 'object') {
                    uninstallRequests![requestKey] = {
                      id: requestData.id || requestKey,
                      packageName: requestData.packageName || '',
                      appName: requestData.appName || '',
                      status: ['pending', 'processing', 'completed', 'failed'].includes(requestData.status) 
                        ? requestData.status as 'pending' | 'processing' | 'completed' | 'failed'
                        : 'pending',
                      requestedAt: Number(requestData.requestedAt) || Date.now(),
                      updatedAt: Number(requestData.updatedAt) || Date.now()
                    };
                  }
                });
              }

              // Processar informações de status do dispositivo
              let statusBar: DeviceStatusBar | undefined = undefined;
              
              if (value.statusBar && typeof value.statusBar === 'object') {
                statusBar = {
                  battery: {
                    charging: Boolean(value.statusBar.battery?.charging),
                    level: Number(value.statusBar.battery?.level) || 0
                  },
                  network: {
                    connected: Boolean(value.statusBar.network?.connected),
                    signalStrength: Number(value.statusBar.network?.signalStrength) || 0,
                    type: value.statusBar.network?.type || ''
                  },
                  wifi: {
                    connected: Boolean(value.statusBar.wifi?.connected),
                    signalStrength: Number(value.statusBar.wifi?.signalStrength) || 0,
                    ssid: value.statusBar.wifi?.ssid || ''
                  },
                  timestamp: Number(value.statusBar.timestamp) || Date.now()
                };
              }
              
              // Processar status de gerenciamento, se disponível
              let management: DeviceManagement | undefined = undefined;

              if (value.management && typeof value.management === 'object') {
                management = {
                  isDeviceOwner: Boolean(value.management.isDeviceOwner),
                  isKioskActive: Boolean(value.management.isKioskActive),
                  isDefaultLauncher: Boolean(value.management.isDefaultLauncher),
                  launcherVersion: value.management.launcherVersion || '',
                  timestamp: Number(value.management.timestamp) || 0
                };
              }

              // Garantir que todos os campos necessários existam
              const device: Device = {
                id: key,
                androidId: value.androidId || undefined,
                serialNumber: value.serialNumber || undefined,
                name: value.name || 'Dispositivo sem nome',
                customName: value.customName || undefined,
                companyName: value.companyName || undefined,
                code: value.code || undefined,
                isActive: value.isActive !== undefined ? Boolean(value.isActive) : true,
                location: {
                  latitude: Number(value.location?.latitude) || 0,
                  longitude: Number(value.location?.longitude) || 0
                },
                lastUpdate: Number(value.lastUpdate) || 0,
                online: Boolean(value.online),
                updateRequested: Boolean(value.updateRequested),
                updateRequestTime: Number(value.updateRequestTime) || 0,
                apkUrl: value.apkUrl || null,
                installedApps,
                statusBar,
                management,
                uninstallRequests
              };
              return device;
            });
            
            setRawDevices(devicesArray);
          } else {
            setRawDevices([]);
          }
        } else {
          setRawDevices([]);
        }
        setLoading(false);
      } catch (err) {
        console.error('Erro ao processar dados:', err);
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
        setLoading(false);
      }
    }, (err) => {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [isAuthenticated]);

  // Reavalia a frescura dos dados mesmo sem novidade no Firebase
  useEffect(() => {
    if (!isAuthenticated) return () => {};

    const timer = setInterval(() => setNow(Date.now()), FRESHNESS_TICK_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated]);

  // O campo `online` gravado pelo dispositivo não é confiável: fica preso em
  // true quando o app morre sem desconectar. Vale o lastUpdate.
  const devices = useMemo(
    () => rawDevices.map(device => ({
      ...device,
      online: device.lastUpdate > 0 && (now - device.lastUpdate) < ONLINE_THRESHOLD_MS
    })),
    [rawDevices, now]
  );

  useEffect(() => {
    if (!isAuthenticated) return () => {};

    const updatesRef = ref(database, 'updates');
    const unsubscribe = onValue(updatesRef, (snapshot) => {
      const pendings: Record<string, boolean> = {};
      // Guarda a atualização mais recente de cada dispositivo, para sinalizar
      // a falha fora da tela de histórico
      const ultimaPorDevice: Record<string, any> = {};
      snapshot.forEach((child) => {
        const update = child.val();
        if (!update?.deviceId) return;
        if (update.status === 'pending') {
          pendings[update.deviceId] = true;
        }
        const atual = ultimaPorDevice[update.deviceId];
        if (!atual || Number(update.createdAt) > Number(atual.createdAt)) {
          ultimaPorDevice[update.deviceId] = update;
        }
      });

      const falhas: Record<string, FailedUpdate> = {};
      Object.entries(ultimaPorDevice).forEach(([deviceId, update]) => {
        if (parseUpdateStatus(update.status) === 'failed') {
          falhas[deviceId] = {
            errorMessage: update.errorMessage || 'Falha na atualização',
            updatedAt: Number(update.updatedAt) || Number(update.createdAt) || 0
          };
        }
      });

      setPendingUpdates(pendings);
      setFailedUpdates(falhas);
    });
    return () => unsubscribe();
  }, [isAuthenticated]);

  const requestUpdate = async (deviceId: string): Promise<boolean> => {
    try {
      await deviceService.requestUpdate(deviceId);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      return false;
    }
  };

  const cancelUpdate = async (deviceId: string): Promise<boolean> => {
    try {
      return await updateService.cancelPendingUpdates(deviceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      return false;
    }
  };

  return { devices, loading, error, requestUpdate, pendingUpdates, failedUpdates, cancelUpdate };
}